import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext';
import { SearchTag } from '@internal/blocks/SearchResultsBlock/TSearchParams';
import {
    aboveMaxSearchResultsLabel,
    maxSearchResults,
} from '@internal/blocks/SearchResultsBlock/maxSearchResults';
import { PageContext } from '@internal/page/PageContext';
import { SystemPageKey } from '@internal/page/SystemPageKey';
import { BrandModelLinks } from '@internal/services/cmsClient';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { useModelLabel } from '@internal/utils/machine/useModelLabel';
import { PageTypeKey } from '@internal/utils/page/PageTypeKey';
import { tracker } from '@internal/utils/tracking/tracker';
import { Typography } from '@renderer-ui-library/mui';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { Fragment, memo, useCallback, useContext, useMemo } from 'react';
import { useMachineTypeName } from 'utils/machine/useMachineTypeName';
import styles from './searchResultsTitle.module.scss';

export interface SearchResultsTitleProps {
    resultsCount: number;
    popularLinks: BrandModelLinks[];
    hasLoadedOnce: boolean;
    hasAnySearchParams: boolean;
}
export const SearchResultsTitle: React.FC<SearchResultsTitleProps> = memo(
    ({ resultsCount, popularLinks, hasLoadedOnce, hasAnySearchParams }) => {
        const { t } = useTranslation(localeNamespace.common);
        const { getMachineTypeName } = useMachineTypeName();
        const { getModelLabel } = useModelLabel();
        const { pageData } = useContext(PageContext);

        const { searchParams } = useSearchParamsContext();

        const filteredPopularLinks = useMemo(() => {
            return popularLinks.filter(link => {
                if (link.type === 'brand') {
                    return (
                        searchParams.machineType === link.machineType &&
                        searchParams.models.length === 0
                    );
                }

                return (
                    searchParams.machineType === link.machineType &&
                    searchParams.brands.includes(link.brand)
                );
            });
        }, [
            popularLinks,
            searchParams.brands,
            searchParams.machineType,
            searchParams.models.length,
        ]);

        const seoDescription = useMemo(() => {
            if (
                pageData?.type === PageTypeKey.SystemPage &&
                pageData?.key === SystemPageKey.SearchResults &&
                searchParams.isOnMachineCategoryPage
            ) {
                return pageData.machineCategoryData?.seoDescription;
            }
            return null;
        }, [pageData, searchParams.isOnMachineCategoryPage]);

        const title = useMemo(() => {
            if (!hasAnySearchParams) {
                return t(translations.serpNoParamsTitle);
            }

            let category: string | null = null;
            const modelsString = searchParams.models
                .map(m => getModelLabel(m.name, m.type))
                .join(', ');
            if (searchParams.brands.length > 0) {
                const joinedBrands = searchParams.brands.join(', ');

                if (searchParams.models.length) {
                    category = `${joinedBrands} ${modelsString}`.trim();
                } else if (searchParams.machineType) {
                    category = `${joinedBrands} ${getMachineTypeName(
                        searchParams.machineType,
                        resultsCount === 1 ? 'singular' : 'plural',
                    )}`;
                } else {
                    category = joinedBrands;
                }
            }

            if (!category) {
                category = `${modelsString} ${
                    searchParams.machineType
                        ? getMachineTypeName(
                              searchParams.machineType,
                              resultsCount === 1 ? 'singular' : 'plural',
                          )
                        : ''
                }`.trim();
            }

            const count =
                resultsCount >= maxSearchResults
                    ? aboveMaxSearchResultsLabel
                    : resultsCount;
            if (searchParams.tags.includes(SearchTag.HotDeals)) {
                return `${t(translations.serpHotDealsTitle, { amount: !hasLoadedOnce ? '' : count.toString(), count: resultsCount })}${category ? `, ${category}` : ''}`;
            }

            return t(translations.searchResultsTitle, {
                category,
                amount: !hasLoadedOnce ? '' : count.toString(),
            });
        }, [
            hasAnySearchParams,
            searchParams.models,
            searchParams.brands,
            searchParams.tags,
            searchParams.machineType,
            resultsCount,
            t,
            hasLoadedOnce,
            getModelLabel,
            getMachineTypeName,
        ]);

        const router = useRouter();

        const handlePopularLinkClick = useCallback(
            (ev: React.SyntheticEvent<HTMLAnchorElement>) => {
                ev.preventDefault();
                tracker.trackEvent({
                    name: 'click',
                    target_name: 'machine_category_page_popular_link.serp',
                    is_interaction: true,
                });
                router.push(ev.currentTarget.href);
            },
            [router],
        );

        const popularLinksType = popularLinks[0]?.type;

        return (
            <div className={styles.wrapper}>
                <div>
                    <Typography
                        variant={hasAnySearchParams ? 'h3' : 'h4'}
                        component="h1"
                    >
                        {title}
                    </Typography>

                    {seoDescription && (
                        <div className={styles.seoDescription}>
                            <BlocksRenderer
                                content={seoDescription}
                                blocks={{
                                    paragraph: ({ children }) => (
                                        <Typography
                                            variant="body1"
                                            color="textSecondary"
                                        >
                                            {children}
                                        </Typography>
                                    ),
                                    link: ({ children, url }) => (
                                        <Link href={url} prefetch={false}>
                                            {children}
                                        </Link>
                                    ),
                                }}
                            />
                        </div>
                    )}
                </div>
                {filteredPopularLinks.length > 0 && (
                    <div className={styles.popularLinksWrapper}>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            noWrap
                            component="div"
                        >
                            <strong>
                                {popularLinksType === 'brand'
                                    ? t(
                                          translations.searchResultsPopularBrandsLabel,
                                      )
                                    : t(
                                          translations.searchResultsPopularModelsLabel,
                                      )}
                                :
                            </strong>{' '}
                            {filteredPopularLinks.map(
                                ({ name, url }, index) => {
                                    const isActive = searchParams.models.some(
                                        m => m.name === name,
                                    );
                                    return (
                                        <Fragment key={name}>
                                            <a
                                                href={url}
                                                onClick={handlePopularLinkClick}
                                                key={name}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color={
                                                        isActive
                                                            ? 'secondary'
                                                            : 'text.primaryLight'
                                                    }
                                                >
                                                    {name}
                                                </Typography>
                                            </a>
                                            {index <
                                                filteredPopularLinks.length -
                                                    1 && <span>, </span>}
                                        </Fragment>
                                    );
                                },
                            )}
                        </Typography>
                    </div>
                )}
            </div>
        );
    },
);

SearchResultsTitle.displayName = 'SearchResultsTitle';
